// src/modules/app-views/invoices/pages/Init/modals/PreviewNFCeModal/index.jsx
// *******************************************************************************
// PREVIEW NFCe MODAL
// *******************************************************************************
// Modal for previewing and printing NFC-e invoices from the list view.
// Shows the DANFE preview with options to print or close.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Modal, Button, Space, ConfigProvider } from 'antd';
import { PrinterOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import invoicesService from 'modules/app-views/invoices/service';
import translateX from 'utils/translateX';
import { getStorageModuleItem } from 'stores/module';
import PreviewNFCeStep from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PreviewNFCeModal = ({ show, setShow, afterClose, data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!loading) {
      setShow(false);
    }
  }, [loading, setShow]);

  const handlePrint = useCallback(async () => {
    setLoading(true);

    try {
      // Check print method configuration (NFCe uses 'nfcePrintMethod')
      const printMethod = getStorageModuleItem('nfcePrintMethod') || 'browser';
      const useBrowserPrint = printMethod === 'browser';

      // Use the modal's container for printing
      await invoicesService.printDanfe('danfe-modal-print-container', {
        invoiceData: data,
        forceDirect: !useBrowserPrint, // ESC/POS only if method is 'escpos'
        forcePreview: useBrowserPrint, // Browser print if method is 'browser'
      });
    } catch (error) {
      console.error('[PreviewNFCeModal] Print error:', error);
    } finally {
      setLoading(false);
    }
  }, [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={translateX('preview_nfce')}
      open={show}
      onCancel={handleClose}
      afterClose={afterClose}
      destroyOnHidden
      centered
      width={440}
      mask={{ closable: !loading }}
      footer={
        <Space>
          <Button icon={<CloseOutlined />} onClick={handleClose} disabled={loading}>
            {translateX('close')}
          </Button>
          <Button
            type="primary"
            icon={loading ? <LoadingOutlined /> : <PrinterOutlined />}
            onClick={handlePrint}
            loading={loading}
          >
            {translateX('print')}
          </Button>
        </Space>
      }
    >
      <PreviewNFCeStep data={data} />
    </Modal>
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (creates modal in React root)
// *******************************************************************************
const DynamicModalWrapper = ({ data, ...props }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;

  return (
    <ConfigProvider theme={config}>
      <PreviewNFCeModal data={data} {...props} />
    </ConfigProvider>
  );
};

const showPreviewNFCeModal = ({ data, onClose } = {}) => {
  // Create container
  const container = document.createElement('div');
  container.id = 'preview-nfce-modal-container';
  document.body.appendChild(container);

  // Create React root
  const root = createRoot(container);

  // Track modal state
  let isModalOpen = true;

  // Handle close and cleanup
  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      container.remove();
      onClose?.();
    }, 0);
  };

  // Render function
  const renderModal = () => {
    root.render(
      <DynamicModalWrapper
        show={isModalOpen}
        setShow={show => {
          if (!show) handleClose();
        }}
        afterClose={handleAfterClose}
        data={data}
      />
    );
  };

  // Initial render
  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showPreviewNFCeModal;
export { PreviewNFCeModal };
